/**
 * The WhatsApp newsletter's quick-reply button contracts — the SHARED grammar
 * for the per-recipient payloads its templates' buttons carry:
 *
 *   nlo.<send_id>.<member_id>   "Tell me more", on the offer message
 *                               (whatsapp-newsletter-offer-only, 2026-10-29;
 *                               migration 262)
 *   nlu.<send_id>.<member_id>   "Not interested", on every newsletter message
 *                               (newsletter-v2-not-interested, 2026-10-31)
 *
 * where `<send_id>` is the `whatsapp_newsletter_sends.id` of the send and
 * `<member_id>` the recipient it was sent to (both uuids).
 *
 * ## "Tell me more"
 *
 * Mario, 2026-10-29: a third newsletter option that is "purely the offer
 * without the newsletter article … only one button … 'Tell me more.' When the
 * member … presses it, it starts the conversation, and then the agent would
 * ask, 'Can I tell you more about this, or how can I help you?' and basically
 * start the sale". Tapping it sends a template `button` reply whose payload is
 * this id; the inbound resolver's `routeNewsletterOfferReply` recognises it,
 * answers with that fixed question and leaves the chat agent a note of which
 * offer it was, so the member's next message is about the offer from the
 * first word.
 *
 * ## "Not interested"
 *
 * On 31 Oct 2026 Meta flagged the studio's account for "Sending spam" after an
 * offer went to 716 people, some of whom did not know who had written to
 * them. Mario's answer: every newsletter template opens with who the studio is
 * and carries a "Not interested" button that "records the opt-out
 * automatically". The inbound resolver's `routeNewsletterOptOutReply` reads
 * this id, applies `set_marketing_opt_out` to the member it names, and answers
 * the tap with one line — so someone who no longer wants the messages can
 * stop them in one tap, rather than block or report the number.
 *
 * ## Why the member is in it
 *
 * Most newsletter recipients have never written to the studio on WhatsApp (on
 * 29 Oct 2026, 2,100 of the 2,187 active members with a number had no WhatsApp
 * binding or link), so the tapping number usually cannot be resolved to a
 * member the way a chat message is — that only happens on their next message,
 * through `member_self_signup`'s phone match. The payload names the recipient
 * instead, and the resolver checks it against the send's own `sent` receipt.
 *
 * Not a credential, like `held.<id>`: only the phone the template was
 * delivered to can tap its button — WhatsApp builds the `button` reply, never
 * the person — and the payload must match a `sent` receipt of that send. A
 * "Tell me more" is answered on the number it came from with words that say
 * nothing about the member; a "Not interested" can only ever opt the member it
 * was sent to OUT of marketing, never back in.
 *
 * This tiny, dependency-free module is the single source of truth so the
 * PRODUCER (`@layer/saga-runtime`'s newsletter send, which sets the
 * per-recipient payloads) and the CONSUMER (the resolver) can never drift. It
 * lives in `@layer/canonical-data` because both sides depend on it — the same
 * placement as `held-message-button.ts` and its siblings.
 *
 * ## Wire format
 *
 *   nlo.<uuid>.<uuid>   — a 3-letter prefix + `.` (4) + 36 + `.` (1) + 36 =
 *   nlu.<uuid>.<uuid>     77 chars, inside every limit WhatsApp providers put
 *                         on a quick-reply payload (Meta's documentation
 *                         states none; others cap it at 128 to 256), so an id
 *                         is never truncated.
 */

/** The literal namespace prefix a "Tell me more" payload carries. */
export const NEWSLETTER_OFFER_PREFIX = "nlo" as const;

/** The visible text of the offer template's "Tell me more" QUICK_REPLY button. */
export const NEWSLETTER_OFFER_BUTTON_TITLE = "Tell me more" as const;

/** The literal namespace prefix a "Not interested" payload carries. */
export const NEWSLETTER_OPT_OUT_PREFIX = "nlu" as const;

/** The visible text of every newsletter template's "Not interested" QUICK_REPLY button. */
export const NEWSLETTER_OPT_OUT_BUTTON_TITLE = "Not interested" as const;

const UUID = "[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}";
const UUID_RE = new RegExp(`^${UUID}$`, "i");
const NEWSLETTER_OFFER_RE = new RegExp(`^nlo\\.(${UUID})\\.(${UUID})$`, "i");
const NEWSLETTER_OPT_OUT_RE = new RegExp(`^nlu\\.(${UUID})\\.(${UUID})$`, "i");

/** `<prefix>.<send_id>.<member_id>`, lower-cased. Throws `<code>_send_id_invalid` / `<code>_member_id_invalid`. */
function buildPayload(prefix: string, code: string, sendId: string, memberId: string): string {
  if (typeof sendId !== "string" || !UUID_RE.test(sendId)) {
    throw new Error(
      `${code}_send_id_invalid: expected a whatsapp_newsletter_sends id, got ${JSON.stringify(sendId)}`,
    );
  }
  if (typeof memberId !== "string" || !UUID_RE.test(memberId)) {
    throw new Error(`${code}_member_id_invalid: expected a member id, got ${JSON.stringify(memberId)}`);
  }
  return `${prefix}.${sendId.toLowerCase()}.${memberId.toLowerCase()}`;
}

/** The send and member a well-formed payload names, or `null`. */
function parsePayload(re: RegExp, raw: string | null | undefined): ParsedNewsletterButtonReply | null {
  if (typeof raw !== "string") return null;
  const m = re.exec(raw.trim());
  const sendId = m?.[1];
  const memberId = m?.[2];
  return sendId === undefined || memberId === undefined
    ? null
    : { sendId: sendId.toLowerCase(), memberId: memberId.toLowerCase() };
}

/** A parsed newsletter button tap — the send and the recipient it was sent to. */
export interface ParsedNewsletterButtonReply {
  readonly sendId: string;
  readonly memberId: string;
}

/** A parsed "Tell me more" tap. */
export type ParsedNewsletterOfferReply = ParsedNewsletterButtonReply;

/** A parsed "Not interested" tap. */
export type ParsedNewsletterOptOutReply = ParsedNewsletterButtonReply;

/**
 * Build the "Tell me more" payload: `nlo.<send_id>.<member_id>`. Throws on a
 * non-uuid id.
 */
export function buildNewsletterOfferReplyId(sendId: string, memberId: string): string {
  return buildPayload(NEWSLETTER_OFFER_PREFIX, "newsletter_offer", sendId, memberId);
}

/**
 * Parse a raw inbound reply id. Returns the send and member for a well-formed
 * `nlo.<uuid>.<uuid>`, or `null` for anything else (a poll row, another
 * template's payload, `null`), so the resolver falls through cleanly.
 */
export function parseNewsletterOfferReplyId(
  raw: string | null | undefined,
): ParsedNewsletterOfferReply | null {
  return parsePayload(NEWSLETTER_OFFER_RE, raw);
}

/**
 * Build the "Not interested" payload: `nlu.<send_id>.<member_id>`. Throws on a
 * non-uuid id.
 */
export function buildNewsletterOptOutReplyId(sendId: string, memberId: string): string {
  return buildPayload(NEWSLETTER_OPT_OUT_PREFIX, "newsletter_opt_out", sendId, memberId);
}

/**
 * Parse a raw inbound reply id. Returns the send and member for a well-formed
 * `nlu.<uuid>.<uuid>`, or `null` for anything else — a "Tell me more" payload
 * included — so the resolver falls through cleanly.
 */
export function parseNewsletterOptOutReplyId(
  raw: string | null | undefined,
): ParsedNewsletterOptOutReply | null {
  return parsePayload(NEWSLETTER_OPT_OUT_RE, raw);
}
